// @flow

import _ from 'lodash';
import { rem } from 'polished';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { BackButtonWrapper } from 'client/components/preRegistration/accountCreate';
import {
  ButtonWrapper,
  RequiredFieldWrapper,
} from 'client/components/preRegistration/registrationForm';
import { isValidPhoneNumber } from 'client/utils/validate';
import { renderPhoneInput, renderBooleanCheckmark } from 'client/components/form/helpers';
import { VIEW_TYPE, FORM_FIELD_NAME } from 'client/components/preRegistration/preRegistration';
import Button from 'client/components/button/button';
import COLOR from 'client/constants/colors';
import RequiredField from 'client/components/form/requiredField';
import {
  PRE_REG_BACK,
  PRE_REG_PHONE_SUBMIT,
  PRE_REG_TEXT_REMINDER_NO,
  PRE_REG_TEXT_REMINDER_YES,
} from 'client/constants/amplitudeTracker';

import { Tracking } from 'web-core/src/tracking';

type Props = {
  backBtn: string,
  commonTranslations: TranslationType,
  confirm: string,
  ctaBtn: string,
  header: string,
  isValid: boolean,
  question: string,
  setView: Function,
  terms: string,
};

type ActionType = 'YES' | 'NO' | 'BACK' | 'SUBMIT';

const ACTIONS = {
  YES: 'YES',
  NO: 'NO',
  BACK: 'BACK',
  SUBMIT: 'SUBMIT',
};

const BorderButton = styled(Button)`
  border: 2px solid ${COLOR.WHITE};
  background: none;
  margin-top: 1rem;
`;

const AcknowledgementContainer = styled.div`
  .confirm {
    display: flex;
    align-items: flex-start;
    font-size: ${rem(12)};
    .sub {
      padding: 0 1rem;
      font-weight: bold;
    }
  }
  .terms {
    font-size: ${rem(12)};
    padding: 0.5rem 0;
  }
`;

const BackButtonNoPadding = styled(BackButtonWrapper)`
  padding: 0;
`;

const renderPhoneField = _.partial(renderPhoneInput, _, {
  placeholder: 'Enter your phone number',
  noLabelAnimate: true,
});

const checkboxValidator = val => (val ? undefined : { path: 'common.error.required' });

const TextReminder = (props: Props) => {
  const {
    backBtn,
    commonTranslations,
    confirm,
    ctaBtn,
    header,
    isValid,
    question,
    setView,
    terms,
  } = props;
  const [showFollowUp, toggleFollowUp] = useState(false);
  const [isDisabled, toggleDisabled] = useState(!isValid);

  useEffect(() => {
    toggleDisabled(!isValid);
  });

  const handleClick = (action: ActionType) => {
    switch (action) {
      case ACTIONS.YES:
        Tracking.track(PRE_REG_TEXT_REMINDER_YES);
        toggleFollowUp(true);
        break;

      case ACTIONS.NO:
        Tracking.track(PRE_REG_TEXT_REMINDER_NO);
        setView(VIEW_TYPE.ACCOUNT_CREATE);
        break;

      case ACTIONS.BACK:
        Tracking.track(PRE_REG_BACK);
        setView(VIEW_TYPE.REGISTRATION_FORM);
        break;

      case ACTIONS.SUBMIT:
        Tracking.track(PRE_REG_PHONE_SUBMIT);
        setView(VIEW_TYPE.ACCOUNT_CREATE);
        break;

      default:
        break;
    }
  };

  const renderYesAndNoButton = () => (
    <React.Fragment>
      <h1 className="medium">{question}</h1>
      <BorderButton clickHandler={() => handleClick(ACTIONS.YES)}>
        {commonTranslations.label.yes}
      </BorderButton>
      <BorderButton clickHandler={() => handleClick(ACTIONS.NO)}>
        {commonTranslations.label.no}
      </BorderButton>
      <BackButtonWrapper onClick={() => handleClick(ACTIONS.BACK)}>{backBtn}</BackButtonWrapper>
    </React.Fragment>
  );

  const renderFollowUp = () => (
    <React.Fragment>
      <h1 className="xsmall">{header}</h1>
      <RequiredFieldWrapper>
        <RequiredField
          key={FORM_FIELD_NAME.PHONE_NUMBER}
          name={FORM_FIELD_NAME.PHONE_NUMBER}
          component={renderPhoneField}
          validate={[isValidPhoneNumber]} />
      </RequiredFieldWrapper>
      <AcknowledgementContainer>
        <div className="confirm">
          <RequiredField
            key={FORM_FIELD_NAME.CONFIRMED}
            name={FORM_FIELD_NAME.CONFIRMED}
            component={renderBooleanCheckmark}
            validate={[checkboxValidator]} />
          <div className="sub">{confirm}</div>
        </div>
        <div className="terms">{terms}</div>
      </AcknowledgementContainer>
      <ButtonWrapper isDisabled={isDisabled} clickHandler={() => handleClick(ACTIONS.SUBMIT)}>
        {ctaBtn}
      </ButtonWrapper>
      <BackButtonNoPadding onClick={() => toggleFollowUp(false)}>{backBtn}</BackButtonNoPadding>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {showFollowUp && renderFollowUp()}
      {!showFollowUp && renderYesAndNoButton()}
    </React.Fragment>
  );
};

export default TextReminder;
